import {ChangeEvent, FC, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {
    BellOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    EditOutlined,
    EnvironmentOutlined,
    SearchOutlined
} from '@ant-design/icons';
import {useJobPool, useJobPoolCount} from "./hooks/useJobPool.ts";
import styled from "styled-components";
import {colors} from "../../util/colors";
import {Button, Input, message, Modal, Radio, Skeleton, Spin, Typography} from "antd";
import {FixedSizeList} from 'react-window';
import InfiniteLoader from "react-window-infinite-loader";
import {IJobDemand, IJobDetails, ILocation, IPosition} from "../../services/types";
import {addSearch, updateSearchById} from "../../services/api";
import {AuthContext} from "../../context/AuthContext.tsx";
import {ProfessionModal} from "../../components/ProfessionModal.tsx";
import {AddressModal} from "../../components/AddressModal.tsx";
import {useMutation} from "@tanstack/react-query";
import {alertOptions, defaultWhereOptions} from "../../util/utils.ts";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {JobPoolItem} from "./components/JobPoolItem.tsx";
import {PromotionalCard} from "./components/PromotionalCard.tsx";
import {ContactCandidateModal} from "../candidateDetail/ContactCandidateModal.tsx";
import {useMediaQuery} from "@uidotdev/usehooks";
import {useSubscriptionEndDate} from "../../hooks/useSubscriptionEndDate.ts";
import {safeLocalStorage} from "../../util/safeStorage.ts";

const {Title} = Typography;
const LIMIT = 20;

export const JobPool: FC = () => {
    const {user} = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [isWhatModalOpen, setIsWhatModalOpen] = useState(false);
    const [isLocationEditorOpen, setIsLocationEditorOpen] = useState(false);
    const [whatSelectedValue, setWhatSelectedValue] = useState<IPosition | null>(null);
    const [whereSelectedValue, setWhereSelectedValue] = useState<ILocation | null>(defaultWhereOptions[0]);

    const [selectedAlertOption, setSelectedAlertOption] = useState(alertOptions[0]);
    const [savedSearchItem, setSavedSearchItem] = useState<IJobDetails | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [isContactModalOpen, setContactModalOpen] = useState(false);
    const [currentJob, setCurrentJob] = useState<IJobDetails | null>(null);

    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const {endDate} = useSubscriptionEndDate();
    const hasActiveSubscription = endDate && !isNaN(endDate.getTime()) && endDate > new Date();
    const itemSize = isMobile ? 220 : 140;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchWhatIdParam = params.get('search_what_id');
        const searchWhatLabel = params.get('search_what_label');
        const searchLocationIdParam = params.get('search_location_id');
        const searchLocationLabel = params.get('search_location_label');

        if (searchWhatIdParam && searchWhatLabel) {
            setWhatSelectedValue({id: Number(searchWhatIdParam), label: searchWhatLabel} as IPosition);
        }

        if (searchLocationIdParam && searchLocationLabel) {
            setWhereSelectedValue({id: Number(searchLocationIdParam), label: searchLocationLabel} as ILocation);
        }
    }, [location.search]);

    const {
        data,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useJobPool(LIMIT, whatSelectedValue?.id || undefined, whereSelectedValue?.id || undefined);

    // Simple one-time check for magic link redirects
    useEffect(() => {
        const magicLinkRedirect = safeLocalStorage.getItem('appRedirectPath');
        if (magicLinkRedirect && magicLinkRedirect !== "/" && magicLinkRedirect !== location.pathname) {
            // Store the path and clear storage before navigation
            const redirectTo = magicLinkRedirect;
            safeLocalStorage.removeItem("appRedirectPath");

            // Navigate once
            navigate(redirectTo);
        }
    }, [location.pathname, navigate])

    const {
        data: numberOfCandidate,
        isLoading
    } = useJobPoolCount(whatSelectedValue || undefined, whereSelectedValue || undefined);

    const updateMutation = useMutation({
        mutationFn: (updatedData: Partial<IJobDetails>) => updateSearchById(savedSearchItem?.id ? savedSearchItem.id : 0, updatedData),
        onSuccess: () => {
            message.success('Eingabe erfolgreich gespeichert');
            setIsAlertModalOpen(false);
        },
        onError: (error) => {
            message.error(`Da ist etwas schiefgelaufen: ${error}`);
        },
    });

    const flattenedData = useMemo(() => {
        const realJobs = data?.pages.flatMap(page => Array.isArray(page) ? page : []) || [];

        // Only add promotional item if user doesn't have active subscription
        if (!hasActiveSubscription) {
            const promotionalItem: IJobDetails = {
                id: -1,
                isPromotional: true,
            } as IJobDetails;

            if (!data || (data?.pages.length === 1 && data.pages[0][0]?.id === 0)) {
                // No real data, return only promotional item
                return [promotionalItem];
            }

            // Append promotional item at the end
            return [...realJobs, promotionalItem];
        }

        return realJobs;
    }, [data, hasActiveSubscription]);

    const itemCount = hasNextPage ? flattenedData.length + 1 : flattenedData.length;

    const loadMoreItems = useCallback(() => {
        if (!isFetchingNextPage) {
            return fetchNextPage().then(() => {
            });
        }
        return Promise.resolve();
    }, [isFetchingNextPage, fetchNextPage]);

    const isItemLoaded = useCallback((index: number) => {
        return !hasNextPage || index < flattenedData.length;
    }, [hasNextPage, flattenedData]);

    const onWhatLooking = () => {
        setIsWhatModalOpen(true);
    };

    const onWhereLooking = useCallback(() => {
        setIsLocationEditorOpen(true);
    }, []);

    const handleSaveProfession = (value: IPosition | null) => {
        const params = new URLSearchParams(location.search);
        if (value?.id) {
            params.set('search_what_id', value.id.toString());
            params.set('search_what_label', value.label);
        } else {
            params.delete('search_what_id');
            params.delete('search_what_label');
        }

        const queryParams = params.toString();
        navigate(`/jobs?${queryParams}`);
        setWhatSelectedValue(value);
    };

    const handleSaveLocation = useCallback((value: ILocation | null) => {
        const params = new URLSearchParams(location.search);
        if (value?.id) {
            params.set('search_location_label', value.label);
            params.set('search_location_id', value.id.toString());
        } else {
            params.delete('search_location_label');
            params.delete('search_location_id');
        }

        const queryParams = params.toString();
        navigate(`/jobs?${queryParams}`);
        setWhereSelectedValue(value);
    }, [navigate, location.search]);

    const onDirectWhatChange = useCallback((value: string | null) => {
        const params = new URLSearchParams(location.search);
        setWhatSelectedValue(null)
        if (!value) {
            params.delete('search_what_id');
            params.delete('search_what_label');
            setWhatSelectedValue(null);

            navigate(`/jobs?${params.toString()}`);
        }
    }, [navigate, location.search]);

    const onDirectWhereChange = useCallback((value: string | null) => {
        const params = new URLSearchParams(location.search);
        setWhereSelectedValue(null)
        if (!value) {
            params.delete('search_location_id');
            params.delete('search_location_label');
            setWhereSelectedValue(null);

            navigate(`/jobs?${params.toString()}`);
        }
    }, [navigate, location.search]);

    const handleSaveFilter = useCallback(async () => {
        if (user!.id) {
            const searchParam: Partial<IJobDemand> = {
                candidate_id: user!.candidate_id,
                users_id: user!.id,
                alert_config_id: 1,
                search_location_id: whereSelectedValue?.id ?? null,
                search_what_id: whatSelectedValue?.id ?? null,
            };

            try {
                const response = await addSearch(searchParam);
                setSavedSearchItem(response);
                setIsAlertModalOpen(true);
            } catch (error) {
                console.error('Error saving search:', error);
                message.error('Failed to save filter. Please try again.');
            }
        } else {
            message.error('User not logged in or missing information.');
        }
    }, [user, whatSelectedValue, whereSelectedValue]);

    const handleSaveAlertSettings = () => {
        if (savedSearchItem) {
            updateMutation.mutate({
                ...savedSearchItem,
                alert_label: selectedAlertOption.label,
                alert_config_id: selectedAlertOption.id
            });
        }
    };

    const handleAfterContact = () => {
        setCurrentJob(null)
        setContactModalOpen(false)
    }

    return (
        <Wrapper>
            <SearchContainer>
                <InputWrapper>
                    <InputLabels>Was?</InputLabels>
                    <StyledInput
                        onClick={onWhatLooking}
                        readOnly={true}
                        placeholder={isMobile ? "Was?" : "Alle, kein Filter"}
                        value={whatSelectedValue?.label || ''}
                        prefix={isMobile ? <SearchOutlined/> : <EditOutlined/>}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onDirectWhatChange(e.target.value)}
                        suffix={
                            <StyledCloseBtn
                                onClick={() => onDirectWhatChange(null)}
                                onTouchStart={() => onDirectWhatChange(null)}
                                style={{color: colors.text, backgroundColor: "white"}}
                                size="small"
                                shape="circle"
                                icon={<CloseOutlined/>}
                            />
                        }
                        size="large"
                    />
                </InputWrapper>
                <InputWrapper>
                    <InputLabels>Wo?</InputLabels>
                    <StyledInput
                        readOnly={true}
                        onClick={onWhereLooking}
                        size="large"
                        placeholder={isMobile ? "Wo?" : "Alle, kein Filter"}
                        value={whereSelectedValue?.label || ''}
                        prefix={isMobile ? <EnvironmentOutlined/> : <EditOutlined/>}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onDirectWhereChange(e.target.value)}
                        suffix={
                            <StyledCloseBtn
                                onClick={() => onDirectWhereChange(null)}
                                onTouchStart={() => onDirectWhereChange(null)}
                                style={{color: colors.text, backgroundColor: "white"}}
                                size="small"
                                shape="circle"
                                icon={<CloseOutlined/>}
                            />
                        }
                    />
                </InputWrapper>
                <SaveFilter onClick={handleSaveFilter}>
                    <BellOutlined/>Suche speichern
                </SaveFilter>
            </SearchContainer>
            <CandidateCount>
                Jobs für Sie: {isLoading ? (
                <Skeleton.Button active={true} style={{width: 128, height: 16}} size='small' shape='default'/>
            ) : (
                `(${numberOfCandidate?.id || 0} Ergebnisse)`
            )}
            </CandidateCount>
            {isFetching && <AbsLoader><Spin size="large"/></AbsLoader>}
                <InfiniteLoader
                    isItemLoaded={isItemLoaded}
                    itemCount={itemCount}
                    loadMoreItems={loadMoreItems}
                >
                    {({onItemsRendered}) => (
                        <FixedSizeList
                            height={window.innerHeight - 176}
                            itemCount={itemCount}
                            itemSize={itemSize}
                            onItemsRendered={onItemsRendered}
                            // ref={(listInstance) => {
                            //     ref(listInstance);
                            //     listRef.current = listInstance;
                            // }}
                            width="100%"
                            // initialScrollOffset={scrollPosition}
                            // onScroll={handleScroll}
                        >
                            {({index, style}) => {
                                const item = flattenedData[index];

                                // Render promotional card for promotional items
                                if (item?.isPromotional || item?.id === -1) {
                                    return <PromotionalCard style={style} key={index} />;
                                }

                                // Render regular job item
                                return (
                                    <div style={style} key={index}>
                                        <JobPoolItem isJobSearchAutomatic={whatSelectedValue?.id === 1762}
                                                     key={item.id} item={item}/>
                                    </div>
                                );
                            }}
                        </FixedSizeList>
                    )}
                </InfiniteLoader>
            <AddressModal
                open={isLocationEditorOpen}
                onClose={() => setIsLocationEditorOpen(false)}
                onSave={handleSaveLocation}
                initialValue={whereSelectedValue}
            />
            <ProfessionModal
                initialValue={whatSelectedValue}
                isModalOpen={isWhatModalOpen}
                handleSaveProf={handleSaveProfession}
                onClose={() => setIsWhatModalOpen(false)}
            />
            {isContactModalOpen && !!currentJob && <ContactCandidateModal
                currentJob={currentJob}
                isModalOpen={isContactModalOpen}
                handleOk={handleAfterContact}
                handleCancel={handleAfterContact}
            />}
            <Modal
                title="Ihre Jobsuche wurde gespeichert"
                open={isAlertModalOpen}
                onCancel={() => setIsAlertModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsAlertModalOpen(false)}
                            onTouchStart={() => setIsAlertModalOpen(false)}>
                        Abbrechen
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleSaveAlertSettings}>
                        Speichern
                    </Button>,
                ]}
            >
                <p><CheckCircleOutlined style={{color: 'green'}}/> Eine Übersicht finden Sie unter: <Link
                    to="/searches">Mein Konto {">"} Jobsuchen</Link></p>

                <Title level={5}>Benachrichtigung per Email</Title>
                <p>Bestimmen Sie wie häufig wir Sie über passende neue Kandidaten informieren</p>
                <Radio.Group
                    style={{display: "flex", flexDirection: 'column'}}
                    onChange={(e) => setSelectedAlertOption(alertOptions.find(option => option.id === e.target.value)!)}
                    value={selectedAlertOption.id}
                >
                    {alertOptions.map(option => (
                        <Radio key={option.id} value={option.id}>{option.label}</Radio>
                    ))}
                </Radio.Group>
            </Modal>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    background: ${colors.bg};
`

const CandidateCount = styled.div`
    font-weight: bold;
    margin: 16px 0;
`
export const SearchContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    @media (max-width: 767px) {
        flex-wrap: wrap;
        gap: 8px;
        justify-content: flex-end;
        margin: 0;
    }

`

export const StyledInput = styled(Input)`
    caret-color: white;
    width: 80%;
    background: white;
    cursor: pointer !important;

    @media (min-width: 992px) {
        &:hover {
            button {
                display: block;
            }
        }
    }

    input {
        cursor: pointer !important;
    }

    @media (max-width: 767px) {
        width: 100%;
    }
`;

const InputWrapper = styled.div`
    width: 100%;
    display: flex;
    gap: 16px;
    align-items: center;
`;

export const AbsLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: calc(50% - 22px);
    top: calc(55%);
`;

export const SaveFilter = styled(Button)`
    color: black;
    border: none;
    display: flex;
    gap: 8px;
    width: 305px;
    cursor: pointer;
    box-shadow: none;
    padding: 0;
    background: transparent !important;

    @media (max-width: 767px) {
        border: none;
        box-shadow: none;
        width: initial;
    }
`;

export const InputLabels = styled.span`
    display: inline-block;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

    @media (max-width: 767px) {
        display: none !important;
    }
`;

export const TableWrapper = styled.div`
    margin-left: -100px;
    margin-right: -100px;
    background: ${colors.bg};
    padding: 0;
    border-radius: 10px;
    @media (max-width: 767px) {
        margin: 0;
        padding: 0;
        padding-top: 2px;
        border-radius: 0;
        background: ${colors.bg};
    }

    ::-webkit-scrollbar {
        display: none;
    }
`;

export const StyledCloseBtn = styled(Button)`
    display: none;
`;
